import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { NgOptimizedImage } from '@angular/common';
import { MatListModule } from '@angular/material/list';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDividerModule } from '@angular/material/divider';
import { Inject } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AUTH } from 'src/app/firebase-injection';
import { MatDialog } from '@angular/material/dialog';
import { filter, startWith, Subscription } from 'rxjs';
import { LoginActionsService } from 'src/app/services/login-actions.service';
import { SidenavToggleService } from 'src/app/services/sidenav-toggle.service';
import { DeckProgressLossDialogComponent } from 'src/app/modules/deck/deck-overview/deck-progress-loss-dialog/deck-progress-loss-dialog.component';
import { Auth, signOut, User as FireAuthUser } from 'firebase/auth';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  imports: [
    MatSidenavModule,
    RouterModule,
    MatListModule,
    MatTabsModule,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatMenuModule,
    MatToolbarModule,
    MatTooltipModule,
    NgOptimizedImage,
  ],
})
export class LayoutComponent implements OnInit, OnDestroy {
  @ViewChild('sidenav') sidenav!: MatSidenav;

  @Input() title: string | undefined = '';
  @Input() goBackButtonUrl: string | undefined;
  @Input() backButtonEnabled = true;
  @Input() subtitle: string | undefined;
  @Input() showUserProfileButton = true;
  @Input() menuButtonDisabled = false;
  @Input() userMenuDisabled = false;
  @Input() checkBeforeNavigation = () => Promise.resolve(true);

  @Output() navigationStopped: EventEmitter<void> = new EventEmitter<void>();
  @Output() goBackButtonClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() menuButtonClick: EventEmitter<void> = new EventEmitter<void>();
  currentFireAuthUser: FireAuthUser | undefined = undefined;
  decksExpanded = true;
  isUserProfilePage: boolean = false;

  links: { name: string; path: string; isActive: boolean }[] = [];
  allLinks = [
    {
      name: $localize`:@@User-Groups:User-Groups`,
      path: '/app/user-groups',
      isActive: false,
    },
    {
      name: $localize`:@@Rewards:Rewards`,
      path: '/app/rewards',
      isActive: false,
    },
    {
      name: $localize`:@@DeckAttributeGroups:Deck Attribute Groups`,
      path: '/app/deck-attribute-groups',
      isActive: false,
    },
    {
      name: $localize`:@@Imprint:Imprint`,
      path: '/app/imprint',
      isActive: false,
    },
    {
      name: $localize`:@@PrivacyPolicy:Privacy Policy`,
      path: '/app/privacy-policy',
      isActive: false,
    },
    {
      name: $localize`:@@FAQ:FAQ`,
      path: '/app/faq',
      isActive: false,
    },
  ];

  attributeGroupLinkPath = '/app/deck-attribute-groups';

  deckLinks = [
    { path: '/app/decks/my-decks', label: $localize`:@@MyDecks:My Decks` },
    {
      path: '/app/decks/explore',
      label: $localize`:@@ExploreDecks:Explore Decks`,
    },
    { path: '/app/decks/import', label: $localize`:@@Import:Import` },
  ];
  activeDeckLink = '/app/decks/my-decks';

  private restrictedRoutes = [
    '/app/decks/my-decks/study-mode/prepare',
    '/app/decks/my-decks/study-mode/study',
    '/app/decks/my-decks/study-mode/verify',
  ];

  private urlSubscription: Subscription | undefined;
  private routerEventsSubscription: Subscription | undefined;
  private sidenavToggleSubscription: Subscription | undefined;
  private currentUserSubscription: Subscription | undefined;
  protected isDesktop = true;

  constructor(
    private sidenavToggleService: SidenavToggleService,
    private loginActionsService: LoginActionsService,
    private router: Router,
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    @Inject(AUTH) private auth: Auth,
    private localStorage: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.currentFireAuthUser = this.auth.currentUser || undefined;

    this.isUserProfilePage = this.router.url.startsWith('/app/user-profile');

    this.routerEventsSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isUserProfilePage =
          event.urlAfterRedirects.startsWith('/app/user-profile');
      }
    });

    this.breakpointObserver.observe([Breakpoints.Web]).subscribe((state) => {
      this.isDesktop = state.matches;

      if (this.isDesktop) {
        this.sidenav?.open();
      } else {
        this.sidenav?.close();
      }
    });

    this.sidenavToggleSubscription = this.sidenavToggleService
      .getOpenSideNavEmitter()
      .subscribe((isOpen) => {
        if (this.isDesktop) {
          this.sidenav?.open();
          return;
        }

        if (isOpen) this.sidenav?.open();
        else this.sidenav?.close();
      });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        if (!this.isDesktop) {
          this.sidenav?.close();
          this.sidenavToggleService.close(); // State sync
        }
      });

    this.currentUserSubscription = this.loginActionsService
      .getCurrentUser()
      .subscribe((user) => {
        if (!user) {
          return;
        }
        this.links = [...this.allLinks];
        if (!user?.canWriteAttributeGroups) {
          this.links = this.links.filter(
            (link) => link.path !== this.attributeGroupLinkPath
          );
        }
        this.updateActiveLinks(this.router.url);
      });

    this.urlSubscription = this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        startWith({ urlAfterRedirects: this.router.url } as NavigationEnd)
      )
      .subscribe((event) => this.updateActiveLinks(event.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    if (this.urlSubscription) {
      this.urlSubscription.unsubscribe();
      this.urlSubscription = undefined;
    }
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
      this.routerEventsSubscription = undefined;
    }
    if (this.sidenavToggleSubscription) {
      this.sidenavToggleSubscription.unsubscribe();
      this.sidenavToggleSubscription = undefined;
    }
    if (this.currentUserSubscription) {
      this.currentUserSubscription.unsubscribe();
      this.currentUserSubscription = undefined;
    }
  }

  private updateActiveLinks(url: string): void {
    if (this.links && this.links.length > 0) {
      this.links = this.links.map((link) => ({
        ...link,
        isActive: url.startsWith(link.path),
      }));
    }
    if (url.startsWith('/app/decks')) {
      this.activeDeckLink =
        this.deckLinks.find((deck) => url.startsWith(deck.path))?.path ??
        '/app/decks/my-decks';
    } else {
      this.activeDeckLink = '';
    }
  }

  navigateToUserProfile(): void {
    this.router.navigate(['/app/user-profile/profile']);
  }

  onNavLinkClick(event: MouseEvent, path: string): void {
    const currentUrl = this.router.url;

    if (this.restrictedRoutes.includes(currentUrl)) {
      event.preventDefault();
      const dialogRef = this.dialog.open(DeckProgressLossDialogComponent, {
        width: '320px',
      });

      dialogRef.afterClosed().subscribe((proceed: boolean) => {
        if (proceed) {
          this.router.navigateByUrl(path);
        }
      });
    } else {
      this.router.navigateByUrl(path);
    }
  }
  getIconForPath(path: string): string {
    if (path.startsWith('/app/decks/my-decks')) return 'home';
    if (path.startsWith('/app/decks/explore')) return 'explore';
    if (path.startsWith('/app/decks/import')) return 'download';
    if (path.startsWith('/app/user-groups')) return 'groups';
    if (path.startsWith('/app/rewards')) return 'card_giftcard';
    return 'chevron_right';
  }
  goToSettings(): void {
    this.router.navigate(['/app/settings']);
  }

  logout(): void {
    this.localStorage.clear();
    signOut(this.auth).then(() => {
      this.router.navigate(['/landing']);
    });
  }
}
